const DEFAULT_RECAPTCHA_SITE_KEY = '6LdAPGoUAAAAAP6I44r4-qn91bofhoDm2Df-Le7Y'
const RECAPTCHA_SITE_KEY =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? DEFAULT_RECAPTCHA_SITE_KEY

declare global {
  interface Window {
    grecaptcha?: {
      ready(callback: () => void): void
      render(
        container: HTMLElement | string,
        parameters: {
          sitekey: string
          size?: string
          theme?: string
          callback?: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
        }
      ): number
      execute(widgetId?: number): void
      reset(widgetId?: number): void
      getResponse(widgetId?: number): string
    }
  }
}

let loadPromise: Promise<void> | null = null

// reCAPTCHA v2 loads with render=explicit for manual control
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'

export const getRecaptchaSiteKey = (): string => RECAPTCHA_SITE_KEY

/**
 * Load reCAPTCHA v2 script.
 * The script is loaded once and cached.
 */
export const loadRecaptcha = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA недоступна в SSR среде'))
  }

  if (window.grecaptcha?.render) {
    return Promise.resolve()
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src^="https://www.google.com/recaptcha/api.js"]`
    )

    if (existingScript) {
      const checkReady = () => {
        if (window.grecaptcha?.render) {
          resolve()
        } else {
          setTimeout(checkReady, 50)
        }
      }
      checkReady()
      return
    }

    const script = document.createElement('script')
    script.src = RECAPTCHA_SRC
    script.async = true
    script.defer = true
    script.onload = () => {
      const checkReady = () => {
        if (window.grecaptcha?.render) {
          resolve()
        } else {
          setTimeout(checkReady, 50)
        }
      }
      checkReady()
    }
    script.onerror = (e) => {
      console.error('[GonkaGame] Failed to load reCAPTCHA script', e)
      script.remove()
      loadPromise = null
      reject(new Error('Не удалось загрузить Google reCAPTCHA'))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

/**
 * Render a visible reCAPTCHA v2 checkbox widget in the given container.
 * Returns the widget ID for later operations.
 */
export const renderRecaptchaWidget = async (
  containerId: string,
  callbacks: {
    onSuccess?: (token: string) => void
    onError?: () => void
    onExpired?: () => void
  } = {}
): Promise<number> => {
  await loadRecaptcha()

  if (!window.grecaptcha) {
    throw new Error('Google reCAPTCHA недоступна')
  }

  return new Promise((resolve) => {
    window.grecaptcha!.ready(() => {
      const widgetId = window.grecaptcha!.render(containerId, {
        sitekey: RECAPTCHA_SITE_KEY,
        theme: 'light',
        size: 'invisible', // Invisible mode to prevent auto-submission on load
        callback: callbacks.onSuccess,
        'error-callback': callbacks.onError,
        'expired-callback': callbacks.onExpired,
      })
      resolve(widgetId)
    })
  })
}

/**
 * Manually trigger reCAPTCHA verification (used when size is 'invisible').
 */
export const executeRecaptchaWidget = (widgetId: number): void => {
  if (window.grecaptcha) {
    window.grecaptcha.execute(widgetId)
  }
}

/**
 * Reset a reCAPTCHA widget to allow re-verification.
 */
export const resetRecaptchaWidget = (widgetId: number): void => {
  if (window.grecaptcha) {
    try {
      window.grecaptcha.reset(widgetId)
    } catch {
      // Ignore reset errors
    }
  }
}

/**
 * Get the current response token from a widget.
 */
export const getRecaptchaResponse = (widgetId: number): string => {
  if (window.grecaptcha) {
    return window.grecaptcha.getResponse(widgetId) || ''
  }
  return ''
}
